<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <DownloadReportDropdown
              :report="report"
              :items="itemDownloadDropdown"
              :actualTotal="totalRows"
            />
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card class="mb-2" style="margin-top: 80px">
      <h5>{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      :searchable="false"
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="false"
      reportKey="summary-ritage-route"
      v-if="hasFilters"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns }">
        <TableViewManager
          :defaultColumnOrder="fields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(routeDepart)="data">
        {{ data.item.departure ? data.item.departure.routeName : '-' }}
      </template>
      <template #cell(departureLocation)="data">
        <span :title="getFullGeofenceNames(data.item.departure)">
          {{ getTruncatedGeofenceNames(data.item.departure) }}
        </span>
      </template>
      <template #cell(routeReturn)="data">
        {{ data.item.return ? data.item.return.routeName : '-' }}
      </template>
      <template #cell(returnLocation)="data">
        <span :title="getFullGeofenceNames(data.item.return)">
          {{ getTruncatedGeofenceNames(data.item.return) }}
        </span>
      </template>
      <template #cell(departureDate)="data">
        {{ data.item ? momentDateShortMonth(data.item.startDate) : '-' }}<br />
      </template>
      <template #cell(ritage)="data">
        {{ data.item ? data.item.ritageCount : '-' }}
      </template>
      <template #cell(totalMileage)="data">
        {{ data.item ? data.item.totalDistanceDriven : '-' }}
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import TableViewManager from '../../components/TableViewManager.vue'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import McBadge from '@/components/McBadge'
import { MixPanel } from '@/mixin/mixpanel'
import {
  GET_REPORT_SUMMARY_RITAGE_ROUTE,
  EXPORT_REPORT_SUMMARY_RITAGE_ROUTE,
} from '@/services/api/report/report'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { mapGetters } from 'vuex'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import DownloadReportDropdown from '../../components/DownloadReportDropdown.vue'

moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    TableViewManager,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    FavoriteReportButton,
    DownloadReportDropdown
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'summary-ritage-route',
      queryCache: new QueryCache(),
    }
  },
  mounted() {
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      const finalFields = []

      this.reportFields.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(field, 'children')) {
          finalFields.push(...field.children)
        } else {
          finalFields.push(field)
        }
      })
      return finalFields.map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, ...otherFilter } = filters
        const startDate = periode.length > 0 ? moment(periode[0] + ' 00:00:00.000') : null
        const endDate = periode.length > 0 ? moment(periode[1] + ' 23:59:59.000') : null
        const model = {
          dateRange: {
            start: startDate,
            end: endDate,
          },
          ...otherFilter,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.isBusy = true
          const res = await this.queryCache.fetch(
            GET_REPORT_SUMMARY_RITAGE_ROUTE,
            {
              filter: {
                ...model,
                ...filter,
              },
            },
            self.reportKey
          )
          const result = res.summaryRitageReport.reportSummaryRitage
          self.totalRows = result?.pagination?.total ?? 0
          self.isBusy = false
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        const detail = this.getReportDetail(this.reportKey) ?? {}
        return detail != {} ? detail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    report() {
      return reportList.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
    hasFilters() {
      return Object.keys(this.filters).length > 0
    },
    itemDownloadDropdown() {
      return [
        {
          name: 'excel',
          label: this.$t('global.action.downloadExcel'),
          onClick: () => {
            this.downloadReport('xlsx')
          },
          icon: this.excelIcon,
        },
        {
          name: 'pdf',
          label: this.$t('global.action.downloadPdf'),
          onClick: () => {
            this.downloadReport('pdf')
          },
          icon: this.pdfIcon,
        },
      ]
    },
  },

  methods: {
    handleColumnReport(fields) {
      this.reportFields = fields
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.tableKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    getFullGeofenceNames(data) {
      if (data && data.geofenceNames) {
        return data.geofenceNames.join(' | ')
      }
      return '-'
    },
    getTruncatedGeofenceNames(data) {
      const names = this.getFullGeofenceNames(data)
      if (names.length > 100) {
        return names.slice(0, 100) + '...'
      }
      return names
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.exportReportSummaryRitage.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }

      const { periode } = this.filters
      const startDate = periode.length > 0 ? moment(periode[0] + ' 00:00:00.000') : null
      const endDate = periode.length > 0 ? moment(periode[1] + ' 23:59:59.000') : null
      const columns = []
      this.reportFields.forEach((item) => {
        if (item.key == 'departureDate') return columns.push('departureDate')
        if (item.key == 'ritage') return columns.push('ritageCount')
        if (item.key == 'totalMileage') return columns.push('totalDistanceDriven')
        if (item.key == 'depart') return columns.push('departure')
        return columns.push(item.key)
      })

      let filterVar = {
        ...model,
        column: columns,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: extension === 'pdf' ? 'Pdf' : 'Excel',
        startDate,
        endDate,
      }

      if (Object.prototype.hasOwnProperty.call(filterVar, 'dateRange')) {
        delete filterVar.dateRange
      }

      this.$apollo
        .query({
          query: EXPORT_REPORT_SUMMARY_RITAGE_ROUTE,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.summaryRitageReportBackground)
        })
    },
  },
}
</script>
